import { useMemo, useState, useDeferredValue } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleX,
  Clock,
  Mail,
  Plus,
  Search,
} from "lucide-react";

import { Card } from "../../components/Card/Card";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Badge } from "../../components/Badge/Badge";
import { Modal } from "../../components/Modal/Modal";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { useToast } from "../../components/Toast/Toast";
import { Menu, MenuItem, MenuSeparator } from "../../components/Menu/Menu";
import {
  Avatar,
  gradientFor,
  initialsFor,
} from "../../components/Avatar/Avatar";

import { useAuth } from "../auth/useAuth";
import { listUsers, AdminUser } from "../users/api";
import { ApiError, describeError } from "../../lib/errors";
import {
  listInvites,
  revokeInvite,
  resendInvite,
  AdminInvite,
} from "./api";
import { NewInviteModal } from "./NewInviteModal";
import { StatusPills, PillSpec } from "./StatusPills";
import styles from "./InvitesPage.module.css";

const STATUSES = ["pending", "accepted", "expired", "revoked", "all"] as const;
type StatusTab = (typeof STATUSES)[number];

type RoleFilter = "all" | "super_admin" | "project_user";

const PAGE_SIZE = 20;

// --- Formatters ------------------------------------------------------------

/** "in 70h" / "in 3d" / "in 18h" — for future timestamps. */
export function humanizeRelativeFuture(
  iso: string,
  now: Date = new Date(),
): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = t - now.getTime();
  if (diff <= 0) return "expired";
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  if (min < 60) return `in ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 72) return `in ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `in ${day}d`;
  const wk = Math.floor(day / 7);
  return `in ${wk}w`;
}

/** Hours until expiry (negative if already expired). */
export function hoursUntil(iso: string, now: Date = new Date()): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return (t - now.getTime()) / (1000 * 60 * 60);
}

/** "today · 14:18", "2 days ago", "Mar 14" — for past timestamps. */
export function humanizeSent(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = now.getTime() - t;
  if (diff < 0) return "just now";
  const day = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (day === 0) {
    const time = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(iso));
    return `today · ${time}`;
  }
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
  }).format(new Date(iso));
}

/** "3 days ago", "Mar 14" — for past timestamps without the "today ·" prefix. */
export function humanizePast(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = now.getTime() - t;
  if (diff < 0) return "just now";
  const day = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (day === 0) return "today";
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
  }).format(new Date(iso));
}

// --- Cell helpers ---------------------------------------------------------

interface ProjectMiniProps {
  name: string | null;
}
function ProjectMini({ name }: ProjectMiniProps) {
  if (!name) {
    return (
      <span
        className={`${styles.projectMini} ${styles.projectMiniPlatform}`}
        aria-label="platform-wide"
      >
        platform-wide
      </span>
    );
  }
  const initials = initialsFor(name).slice(0, 1);
  return (
    <span className={styles.projectMini}>
      <span
        className={styles.projectMiniIcon}
        aria-hidden="true"
        style={{ background: gradientFor(name) }}
      >
        {initials}
      </span>
      {name}
    </span>
  );
}

interface EmIconProps {
  status: AdminInvite["status"];
}
function EmIcon({ status }: EmIconProps) {
  const cls =
    status === "pending"
      ? styles.emIconPending
      : status === "accepted"
        ? styles.emIconAccepted
        : styles.emIconFinal;
  let icon;
  if (status === "accepted") icon = <Check size={14} aria-hidden="true" />;
  else if (status === "expired")
    icon = <Clock size={14} aria-hidden="true" />;
  else if (status === "revoked")
    icon = <CircleX size={14} aria-hidden="true" />;
  else icon = <Mail size={14} aria-hidden="true" />;
  return (
    <span className={`${styles.emIcon} ${cls}`} aria-hidden="true">
      {icon}
    </span>
  );
}

interface FilterChipProps {
  label: string;
  value: string;
  items: MenuItem[];
}
function FilterChip({ label, value, items }: FilterChipProps) {
  return (
    <Menu
      items={items}
      placement="bottom-start"
      aria-label={`${label} filter`}
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${label} filter — ${value}`}
          className={styles.filterChip}
          onClick={toggle}
        >
          <span className={styles.filterChipLabel}>{label}:</span>
          <span className={styles.filterChipValue}>{value}</span>
          <ChevronDown
            width={11}
            height={11}
            aria-hidden="true"
            className={styles.filterChipIcon}
          />
        </button>
      )}
    />
  );
}

// --- Page -----------------------------------------------------------------

export function InvitesPage() {
  const qc = useQueryClient();
  const { push } = useToast();
  const { user, projects } = useAuth();
  const isSuper = user?.role === "super_admin";

  // Filter state.
  const [tab, setTab] = useState<StatusTab>("pending");
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [confirm, setConfirm] = useState<AdminInvite | null>(null);
  const [newInviteOpen, setNewInviteOpen] = useState(false);

  // Fetch ALL invites once and compute counts + filters client-side.
  // (Backend `?status=` exists but per-status counts don't — v1 keeps it simple.)
  const { data: invites = [], isLoading } = useQuery({
    queryKey: ["invites", "all"],
    queryFn: () => listInvites(),
  });

  // Resolve invited_by → user email/name. Super admins can list all users;
  // members get an empty list (falls back to "Unknown").
  const { data: usersList = [] } = useQuery({
    queryKey: ["users"],
    queryFn: listUsers,
    enabled: isSuper,
    staleTime: 60_000,
  });
  const userById = useMemo(() => {
    const map = new Map<string, AdminUser>();
    for (const u of usersList) map.set(u.id, u);
    return map;
  }, [usersList]);

  // Project name lookup.
  function projectNameOf(gameId: string | null): string | null {
    if (!gameId) return null;
    const p = projects.find((pp) => pp.id === gameId);
    return p ? p.name : gameId;
  }

  // --- Mutations ----------------------------------------------------------

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeInvite(id),
    onSuccess: (_data, inviteId) => {
      qc.invalidateQueries({ queryKey: ["invites"] });
      // If the invite belongs to a project, also refresh that project's
      // activity feed so the new "invite.revoked" event surfaces.
      const inv = invites.find((i) => i.id === inviteId);
      if (inv?.game_id) {
        qc.invalidateQueries({
          queryKey: ["project", inv.game_id, "activity"],
        });
      }
      push({ variant: "success", message: "Invite revoked." });
      setConfirm(null);
    },
    onError: (err) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const resendMut = useMutation({
    mutationFn: ({ id }: { id: string; email: string }) => resendInvite(id),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["invites"] });
      // If the invite belongs to a project, also refresh that project's
      // activity feed so the new "invite.resent" event surfaces.
      const inv = invites.find((i) => i.id === vars.id);
      if (inv?.game_id) {
        qc.invalidateQueries({
          queryKey: ["project", inv.game_id, "activity"],
        });
      }
      push({ variant: "success", message: `Invite re-sent to ${vars.email}.` });
    },
    onError: (err) => {
      // 409 invite_not_pending — the invite was accepted / revoked / expired
      // between list-load and click; steer the admin toward a fresh invite.
      if (err instanceof ApiError && err.code === "invite_not_pending") {
        push({
          variant: "warn",
          message: "This invite can't be resent — send a new one.",
        });
        return;
      }
      // 404 not_found — the invite no longer exists.
      if (err instanceof ApiError && err.code === "not_found") {
        push({
          variant: "warn",
          message: "This invite no longer exists — send a new one.",
        });
        return;
      }
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  // --- Derived data -------------------------------------------------------

  // Per-status counts (computed from the full list, ignoring search/role/
  // project filters — pills are the *primary* axis).
  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, expired: 0, revoked: 0, all: 0 };
    for (const inv of invites) {
      c.all++;
      c[inv.status]++;
    }
    return c;
  }, [invites]);

  // Apply tab + search + role + project filters.
  const filtered = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    let rows = invites.slice();
    if (tab !== "all") rows = rows.filter((r) => r.status === tab);
    if (roleFilter !== "all") rows = rows.filter((r) => r.role === roleFilter);
    if (projectFilter !== "all") {
      if (projectFilter === "__platform__") {
        rows = rows.filter((r) => r.game_id === null);
      } else {
        rows = rows.filter((r) => r.game_id === projectFilter);
      }
    }
    if (q) {
      rows = rows.filter((r) => {
        const proj = projectNameOf(r.game_id)?.toLowerCase() ?? "";
        return (
          r.email.toLowerCase().includes(q) ||
          proj.includes(q) ||
          (proj === "" && "platform".includes(q))
        );
      });
    }
    // Sort: pending first (newest first), then non-pending (newest first).
    rows.sort((a, b) => {
      const aPending = a.status === "pending" ? 0 : 1;
      const bPending = b.status === "pending" ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
    return rows;
    // projectNameOf depends on `projects` array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invites, tab, deferredQuery, roleFilter, projectFilter, projects]);

  // Clamp page to a valid range whenever filter result size changes.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  // --- UI helpers ---------------------------------------------------------

  const pillSpecs: PillSpec<StatusTab>[] = isSuper
    ? [
        { id: "pending", label: "Pending", count: counts.pending },
        { id: "accepted", label: "Accepted", count: counts.accepted },
        { id: "expired", label: "Expired", count: counts.expired },
        { id: "revoked", label: "Revoked", count: counts.revoked },
        { id: "all", label: "All", count: counts.all },
      ]
    : [
        { id: "pending", label: "Pending", count: counts.pending },
        { id: "accepted", label: "Accepted", count: counts.accepted },
        { id: "all", label: "All", count: counts.all },
      ];

  const roleMenuItems: MenuItem[] = [
    { label: "All", onSelect: () => setRoleFilter("all") },
    { label: "Super admin", onSelect: () => setRoleFilter("super_admin") },
    { label: "Member", onSelect: () => setRoleFilter("project_user") },
  ];
  const projectMenuItems: MenuItem[] = [
    { label: "All", onSelect: () => setProjectFilter("all") },
    {
      label: "Platform-wide",
      onSelect: () => setProjectFilter("__platform__"),
    },
    ...projects.map((p) => ({
      label: p.name,
      onSelect: () => setProjectFilter(p.id),
    })),
  ];

  function projectFilterValue(): string {
    if (projectFilter === "all") return "All";
    if (projectFilter === "__platform__") return "Platform-wide";
    return projects.find((p) => p.id === projectFilter)?.name ?? "All";
  }

  function rowMenuFor(inv: AdminInvite): (MenuItem | MenuSeparator)[] {
    if (inv.status === "pending") {
      return [
        {
          label: "Resend",
          onSelect: () =>
            resendMut.mutate({ id: inv.id, email: inv.email }),
        },
        { separator: true },
        {
          label: "Revoke",
          variant: "danger",
          onSelect: () => setConfirm(inv),
        },
      ];
    }
    if (inv.status === "accepted") {
      return [];
    }
    // expired / revoked
    return [
      {
        label: "Re-invite",
        onSelect: () =>
          resendMut.mutate({ id: inv.id, email: inv.email }),
      },
    ];
  }

  function emailSubLine(inv: AdminInvite): string {
    const sent = humanizeSent(inv.created_at);
    if (inv.status === "pending") {
      return `sent ${sent} · expires ${humanizeRelativeFuture(inv.expires_at)}`;
    }
    if (inv.status === "accepted" && inv.accepted_at) {
      return `sent ${sent} · accepted ${humanizePast(inv.accepted_at)}`;
    }
    if (inv.status === "expired") {
      return `sent ${sent} · expired ${humanizePast(inv.expires_at)}`;
    }
    if (inv.status === "revoked" && inv.revoked_at) {
      const inviter = userById.get(inv.invited_by);
      const who = inviter?.email ?? "admin";
      return `sent ${sent} · revoked by ${who}`;
    }
    return `sent ${sent}`;
  }

  function expiresCell(inv: AdminInvite) {
    if (inv.status !== "pending") {
      return <span className={styles.expires}>—</span>;
    }
    const hrs = hoursUntil(inv.expires_at);
    const soon = hrs < 24 && hrs > 0;
    return (
      <span
        className={`${styles.expires} ${soon ? styles.expiresSoon : ""}`}
      >
        {humanizeRelativeFuture(inv.expires_at)}
      </span>
    );
  }

  // --- Render --------------------------------------------------------------

  // Reset page to 1 whenever filters change.
  function applyTab(next: StatusTab) {
    setTab(next);
    setPage(1);
  }

  const trimmedQuery = deferredQuery.trim();
  const isFilteredView =
    tab !== "all" ||
    !!trimmedQuery ||
    roleFilter !== "all" ||
    projectFilter !== "all";
  const showLoading = isLoading;
  const showNoMatches =
    !isLoading && filtered.length === 0 && invites.length > 0 && isFilteredView;
  const showEmptyState =
    !isLoading && filtered.length === 0 && (invites.length === 0 || !showNoMatches);

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Platform" }, { label: "Invites" }]}
        title="Invites"
        actions={
          isSuper ? (
            <Button
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setNewInviteOpen(true)}
            >
              New invite
            </Button>
          ) : undefined
        }
      />
      <div className={styles.page}>
        <div className={styles.body}>
        <StatusPills
          pills={pillSpecs}
          active={tab}
          onChange={applyTab}
          ariaLabel="Invite status filter"
        />

        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Input
              type="search"
              aria-label="Search invites"
              placeholder={
                isSuper ? "Search by email or project…" : "Search…"
              }
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              leadingIcon={<Search size={14} aria-hidden="true" />}
            />
          </div>
          <FilterChip
            label="Role"
            value={
              roleFilter === "all"
                ? "All"
                : roleFilter === "super_admin"
                  ? "Super admin"
                  : "Member"
            }
            items={roleMenuItems}
          />
          {isSuper && (
            <FilterChip
              label="Project"
              value={projectFilterValue()}
              items={projectMenuItems}
            />
          )}
        </div>

        <Card className={styles.tableCard}>
          {showLoading ? (
            <div className={styles.loading}>Loading…</div>
          ) : showNoMatches ? (
            <div className={styles.noMatches}>
              No matches for «{trimmedQuery || "filter"}».
            </div>
          ) : showEmptyState ? (
            <EmptyStateForTab
              tab={tab}
              onNewInvite={() => setNewInviteOpen(true)}
              showCta={isSuper}
            />
          ) : (
            <InvitesTable
              rows={pageRows}
              isSuper={!!isSuper}
              tab={tab}
              userById={userById}
              projectNameOf={projectNameOf}
              emailSubLine={emailSubLine}
              expiresCell={expiresCell}
              rowMenuFor={rowMenuFor}
            />
          )}
        </Card>

        {pageCount > 1 && (
          <div className={styles.pagination}>
            <div>
              Showing {startIdx + 1}–{startIdx + pageRows.length} of{" "}
              {filtered.length} invites
            </div>
            <div className={styles.pages}>
              <button
                type="button"
                className={styles.pageBtn}
                aria-label="Previous page"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={12} aria-hidden="true" />
              </button>
              <span className={styles.pageIndicator}>
                {currentPage} / {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                aria-label="Next page"
                disabled={currentPage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                <ChevronRight size={12} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>

      {confirm && (
        <Modal
          open
          onClose={() => setConfirm(null)}
          title={`Revoke invite to ${confirm.email}?`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => revokeMut.mutate(confirm.id)}
                disabled={revokeMut.isPending}
              >
                Revoke
              </Button>
            </>
          }
        >
          <p>
            {confirm.email} won't be able to use this invite link anymore. You
            can send a new invite later.
          </p>
        </Modal>
      )}

      <NewInviteModal
        open={newInviteOpen}
        onClose={() => setNewInviteOpen(false)}
      />
      </div>
    </>
  );
}

// --- Sub-components -------------------------------------------------------

interface InvitesTableProps {
  rows: AdminInvite[];
  isSuper: boolean;
  tab: StatusTab;
  userById: Map<string, AdminUser>;
  projectNameOf: (gameId: string | null) => string | null;
  emailSubLine: (inv: AdminInvite) => string;
  expiresCell: (inv: AdminInvite) => React.ReactNode;
  rowMenuFor: (inv: AdminInvite) => (MenuItem | MenuSeparator)[];
}
function InvitesTable({
  rows,
  isSuper,
  tab,
  userById,
  projectNameOf,
  emailSubLine,
  expiresCell,
  rowMenuFor,
}: InvitesTableProps) {
  // Insert a 2px divider between the last "pending" row and the first
  // non-pending row when the "All" filter is active.
  let dividerInserted = false;

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: "30%" }}>Invite</th>
            <th>Role &amp; project</th>
            {isSuper && <th>Invited by</th>}
            <th>Status</th>
            <th>Expires</th>
            <th
              className={styles.actionsCell}
              style={{ width: 60 }}
              aria-label="Actions"
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((inv, i) => {
            const isFinal = inv.status !== "pending";
            const prev = rows[i - 1];
            const isGroupBreak =
              tab === "all" &&
              !dividerInserted &&
              prev !== undefined &&
              prev.status === "pending" &&
              isFinal;
            if (isGroupBreak) dividerInserted = true;
            const rowCls = [
              styles.row,
              isFinal ? styles.rowMuted : "",
              isGroupBreak ? styles.rowGroupBreak : "",
            ]
              .filter(Boolean)
              .join(" ");
            const inviter = userById.get(inv.invited_by);
            const inviterLabel = inviter?.email ?? "Unknown";
            const inviterName = inviter?.name ?? inviter?.email ?? "Unknown";
            const projName = projectNameOf(inv.game_id);
            return (
              <tr key={inv.id} className={rowCls}>
                <td>
                  <div className={styles.emailCell}>
                    <EmIcon status={inv.status} />
                    <div className={styles.emailInfo}>
                      <span className={styles.emailAddr}>{inv.email}</span>
                      <span className={styles.emailSub}>
                        {emailSubLine(inv)}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className={styles.roleProject}>
                    <Badge
                      variant={inv.role === "super_admin" ? "admin" : "user"}
                    >
                      {inv.role === "super_admin" ? "Super admin" : "Member"}
                    </Badge>
                    <ProjectMini name={projName} />
                  </div>
                </td>
                {isSuper && (
                  <td>
                    <div className={styles.invitedBy}>
                      <Avatar name={inviterName} size={20} />
                      {inviterLabel}
                    </div>
                  </td>
                )}
                <td>
                  {inv.status === "pending" && (
                    <Badge variant="warning" dot>
                      Pending
                    </Badge>
                  )}
                  {inv.status === "accepted" && (
                    <Badge variant="success">Accepted</Badge>
                  )}
                  {inv.status === "expired" && (
                    <Badge variant="neutral">Expired</Badge>
                  )}
                  {inv.status === "revoked" && (
                    <Badge variant="neutral">Revoked</Badge>
                  )}
                </td>
                <td>{expiresCell(inv)}</td>
                <td className={styles.actionsCell}>
                  {rowMenuFor(inv).length > 0 && (
                    <Menu
                      items={rowMenuFor(inv)}
                      aria-label={`Actions for invite to ${inv.email}`}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface EmptyStateForTabProps {
  tab: StatusTab;
  onNewInvite: () => void;
  showCta: boolean;
}
function EmptyStateForTab({
  tab,
  onNewInvite,
  showCta,
}: EmptyStateForTabProps) {
  // Distinct copy per filter — mockup L755-L757 + L704-L706.
  const cfg: Record<
    StatusTab,
    {
      icon: React.ReactNode;
      title: string;
      description: string;
      cta?: boolean;
    }
  > = {
    pending: {
      icon: <Check aria-hidden="true" />,
      title: "No pending invites",
      description:
        "Everyone you've invited has either accepted or had their invite expire. Send a new one anytime.",
      cta: true,
    },
    accepted: {
      icon: <Check aria-hidden="true" />,
      title: "No accepted invites",
      description: "Once someone accepts, you'll see them here.",
    },
    expired: {
      icon: <Clock aria-hidden="true" />,
      title: "No expired invites",
      description:
        "Invites stay valid for 72 hours. Expired ones will show up here for re-invite.",
    },
    revoked: {
      icon: <CircleX aria-hidden="true" />,
      title: "No revoked invites",
      description: "Invites you revoke manually will appear here.",
    },
    all: {
      icon: <Mail aria-hidden="true" />,
      title: "No invites yet",
      description: "Invite your first teammate to get started.",
      cta: true,
    },
  };
  const c = cfg[tab];
  return (
    <div className={styles.emptyWrap}>
      <EmptyState
        icon={c.icon}
        title={c.title}
        description={c.description}
        action={
          c.cta && showCta ? (
            <Button
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={onNewInvite}
            >
              New invite
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}
